import React, { useState } from 'react';
import {
  Box,
  Button,
  HStack,
  VStack,
  Text,
  IconButton,
  Input,
  Field,
  useDisclosure,
  Table,
} from '@chakra-ui/react';
import { FiPlus, FiCopy, FiTrash2 } from 'react-icons/fi';
import { toaster } from '../components/ui/toaster';
import { Avatar } from '../components/ui/avatar';
import { PageWrapper } from '../components/PageWrapper';
import { EmptyState } from '../components/EmptyState';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
} from '../components/ui/modal';

// Mock Nostr functions - to be replaced with applesauce later
const nip19 = {
  decode: (npub: string) => {
    if (!npub.startsWith('npub1')) {
      throw new Error('Invalid npub format');
    }
    return {
      type: 'npub',
      data: npub.substring(5, 64) // Mock decoded data
    };
  },
  npubEncode: (publicKey: string) => {
    return 'npub1' + publicKey.substring(0, 59);
  }
};

interface Contact {
  id: string;
  displayName: string;
  publicKey: string;
  npub: string;
  profileImage?: string;
}

function ContactsPage() {
  const [contacts, setContacts] = useState<Contact[]>([
    {
      id: '1',
      displayName: 'Alice',
      publicKey: '7e7e9c42a91bfef19fa929e5fda1b72e0ebc1a4c1141673e2794234d86addf4e',
      npub: 'npub10el5rp4frvl0r8lf9827l6xm9e8tr2dvy29x8e3eg533mzx6ma8qnfnvpv',
      profileImage: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Alice'
    },
    {
      id: '2',
      displayName: 'Bob',
      publicKey: '6e468422dfb74a5738702a8823b9b28168abab8655faacb6853cd0ee15deee93',
      npub: 'npub1de5xyghdk696wuczs2yzhjun2xs6h2ux2h7j636r56wz20wa66fs4y90nc',
      profileImage: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Bob'
    }
  ]);

  const [newContactNpub, setNewContactNpub] = useState('');
  const [newContactName, setNewContactName] = useState('');
  const { isOpen, onOpen, onClose } = useDisclosure();

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toaster.create({
      title: `${label} copied`,
      type: 'info',
      duration: 1500,
    });
  };

  const addContact = () => {
    try {
      let publicKey: string;
      
      if (newContactNpub.startsWith('npub1')) {
        const decoded = nip19.decode(newContactNpub);
        if (decoded.type !== 'npub') {
          throw new Error('Invalid npub format');
        }
        publicKey = decoded.data as string;
      } else {
        publicKey = newContactNpub;
      }

      const npub = newContactNpub.startsWith('npub1') 
        ? newContactNpub 
        : nip19.npubEncode(publicKey);

      const newContact: Contact = {
        id: crypto.randomUUID(),
        displayName: newContactName || `Contact ${contacts.length + 1}`,
        publicKey,
        npub,
        profileImage: `https://api.dicebear.com/7.x/avataaars/svg?seed=${publicKey}`
      };

      setContacts([...contacts, newContact]);
      setNewContactNpub('');
      setNewContactName('');
      onClose();

      toaster.create({
        title: 'Contact added',
        type: 'success',
        duration: 2000,
      });
    } catch (error) {
      toaster.create({
        title: 'Invalid public key or npub',
        type: 'error',
        duration: 3000,
      });
    }
  };

  const removeContact = (id: string) => {
    setContacts(contacts.filter(c => c.id !== id));
    toaster.create({
      title: 'Contact removed',
      type: 'info',
      duration: 2000,
    });
  };

  return (
    <PageWrapper>
      <HStack justify="space-between">
        <Text fontSize="2xl" fontWeight="bold">Contacts</Text>
        <Button
          colorPalette="blue"
          onClick={onOpen}
        >
          <FiPlus /> Add Contact
        </Button>
      </HStack>

          <Box overflowX="auto">
            <Table.Root>
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>Profile</Table.ColumnHeader>
                  <Table.ColumnHeader>Display Name</Table.ColumnHeader>
                  <Table.ColumnHeader>Public Key (npub)</Table.ColumnHeader>
                  <Table.ColumnHeader>Actions</Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {contacts.map((contact) => (
                  <Table.Row key={contact.id}>
                    <Table.Cell>
                      <Avatar
                        size="sm"
                        src={contact.profileImage}
                        name={contact.displayName}
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontWeight="medium">{contact.displayName}</Text>
                    </Table.Cell>
                    <Table.Cell>
                      <HStack>
                        <Text fontSize="sm" fontFamily="mono" noOfLines={1} maxW="400px">
                          {contact.npub}
                        </Text>
                        <IconButton
                          aria-label="Copy npub"
                          size="sm"
                          variant="ghost"
                          onClick={() => copyToClipboard(contact.npub, 'npub')}
                        >
                          <FiCopy />
                        </IconButton>
                      </HStack>
                    </Table.Cell>
                    <Table.Cell>
                      <IconButton
                        aria-label="Remove contact"
                        size="sm"
                        variant="ghost"
                        colorPalette="red"
                        onClick={() => removeContact(contact.id)}
                      >
                        <FiTrash2 />
                      </IconButton>
                    </Table.Cell>
                  </Table.Row>
                ))}
                {contacts.length === 0 && (
                  <EmptyState
                    colSpan={4}
                    message="No contacts yet. Add your first contact to get started."
                  />
                )}
              </Table.Body>
            </Table.Root>
          </Box>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalHeader>Add New Contact</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
            <VStack gap={4}>
              <Field.Root>
                <Field.Label>Display Name</Field.Label>
                <Input
                  placeholder="Enter contact name"
                  value={newContactName}
                  onChange={(e) => setNewContactName(e.target.value)}
                />
              </Field.Root>
              <Field.Root required>
                <Field.Label>Public Key (npub or hex)</Field.Label>
                <Input
                  placeholder="npub1... or hex public key"
                  value={newContactNpub}
                  onChange={(e) => setNewContactNpub(e.target.value)}
                />
              </Field.Root>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button 
              colorPalette="blue" 
              onClick={addContact}
              disabled={!newContactNpub}
            >
              Add Contact
            </Button>
          </ModalFooter>
      </Modal>
    </PageWrapper>
  );
}

export default ContactsPage;